import React, { useCallback, useEffect, useState, useContext } from 'react';
import { ThemeContext, ThemeProvider } from 'styled-components';
import { BackgroundColor, BrandColor, browserFocusOutlineColor, ComplementaryColor, FunctionColor, SocialColor, SupportingColor, } from '../tokens';
import { ColorMode } from './type';
import { LUXV1Theme } from './themes';
import { DARK_THEME_CLASS } from './constant';
const setDarkBodyClass = (mode) => {
    if (document === null || document === void 0 ? void 0 : document.body) {
        if (mode === ColorMode.DARK)
            document.body.classList.add(DARK_THEME_CLASS);
        else
            document.body.classList.remove(DARK_THEME_CLASS);
    }
};
/**
 * Context Provider for color mode (dark, light) It uses ThemeProvider of styled-components underneath
 * @param initialColorMode Initial color mode
 * @param children React children
 */
export const LUXProvider = ({ initialColorMode, children, LUXTheme = LUXV1Theme, isRootLevel = true, }) => {
    const [controlledValue, setControlledValue] = useState(initialColorMode);
    useEffect(() => {
        setControlledValue(initialColorMode);
        if (isRootLevel) {
            setDarkBodyClass(initialColorMode);
        }
        return () => {
            if (isRootLevel) {
                setDarkBodyClass(ColorMode.LIGHT);
            }
        };
    }, [initialColorMode, LUXTheme, isRootLevel]);
    const toggleColorMode = useCallback(() => setControlledValue(prevMode => {
        const newMode = prevMode === ColorMode.DARK ? ColorMode.LIGHT : ColorMode.DARK;
        if (isRootLevel) {
            setDarkBodyClass(newMode);
        }
        return newMode;
    }), [isRootLevel]);
    const setColorMode = useCallback((mode) => {
        setControlledValue(mode);
        if (isRootLevel) {
            setDarkBodyClass(mode);
        }
    }, [isRootLevel]);
    const context = {
        colorMode: controlledValue,
        currentTheme: LUXTheme.displayName,
        ...{
            brandColor: BrandColor,
            socialColor: SocialColor,
            functionColor: FunctionColor,
            supportingColor: SupportingColor,
            backgroundColor: BackgroundColor,
            complementaryColor: ComplementaryColor,
            focusOutlineColor: browserFocusOutlineColor,
        },
        toggleColorMode,
        setColorMode,
    };
    return (React.createElement(ThemeProvider, { theme: context },
        React.createElement(LUXTheme, { colorMode: controlledValue, injectStylesToRootLevel: isRootLevel }, children)));
};
const isColorModeContext = (theme) => {
    return !!(theme === null || theme === void 0 ? void 0 : theme.colorMode);
};
/**
 * Context Consumer for color mode (dark, light) It will return Light theme when no context is available
 * TODO: Change name to something compliant with actual provider
 */
export const useColorMode = () => {
    const context = useContext(ThemeContext);
    if (isColorModeContext(context)) {
        return context;
    }
    // else return default theme
    return {
        colorMode: ColorMode.LIGHT,
        ...{
            brandColor: BrandColor,
            socialColor: SocialColor,
            functionColor: FunctionColor,
            supportingColor: SupportingColor,
            backgroundColor: BackgroundColor,
            complementaryColor: ComplementaryColor,
            focusOutlineColor: browserFocusOutlineColor,
        },
        toggleColorMode: () => { },
        setColorMode: () => { },
    };
};
/**
 * Shortcut for LUXProvider initialColormode = Dark, isRootLevel={false}
 * @param children React children
 */
export const DarkModeProvider = ({ children }) => {
    return (React.createElement(LUXProvider, { initialColorMode: ColorMode.DARK, isRootLevel: false }, children));
};
/**
 * Shortcut for LUXProvider initialColormode = Light, isRootLevel={false}
 * @param children React children
 */
export const LightModeProvider = ({ children }) => {
    return (React.createElement(LUXProvider, { initialColorMode: ColorMode.LIGHT, isRootLevel: false }, children));
};
//# sourceMappingURL=provider.js.map